/**
 * ec-restock-form — "Avise-me quando chegar"
 *
 * Custom element porque a troca de variante no Dawn não re-renderiza o
 * `buy-buttons`: o product-info.js troca uma allowlist fixa de ids e publica
 * `variant-change`. Quem decide a visibilidade do formulário é este arquivo.
 */
if (!customElements.get('ec-restock-form')) {
  customElements.define(
    'ec-restock-form',
    class RestockForm extends HTMLElement {
      connectedCallback() {
        this.form   = this.querySelector('form');
        this.msg    = this.querySelector('[data-msg]');
        this.count  = this.querySelector('[data-count]');
        this.botao  = this.querySelector('button[type="submit"]');
        this.enviando = false;

        this.form.addEventListener('submit', this.onSubmit.bind(this));

        // constants.js carrega sem `defer` no theme.liquid, então PUB_SUB_EVENTS
        // já existe quando este arquivo (que é deferido) roda. O fallback cobre
        // uma eventual mudança na ordem de carga.
        const EV =
          typeof PUB_SUB_EVENTS !== 'undefined'
            ? PUB_SUB_EVENTS
            : { variantChange: 'variant-change', optionValueSelectionChange: 'option-value-selection-change' };

        /*
         * Duas assinaturas, e a primeira não é redundante.
         *
         * Quando a combinação escolhida não existe, o product-info.js chama
         * setUnavailable() e **retorna antes de publicar `variant-change`**. Se
         * só ouvíssemos a segunda, o formulário ficaria exibido com o id da
         * variante anterior — oferecendo aviso para um sabor que a pessoa nem
         * está vendo.
         *
         * Escondendo já na mexida do seletor, o estado padrão passa a ser
         * "escondido" e só o `variant-change` reexibe. Sem evento, fica
         * escondido — que é o certo: não há variante para avisar.
         */
        this.unsubSelecao = subscribe(EV.optionValueSelectionChange, ({ data }) => {
          if (!this.pertenceAMim(data?.target)) return;
          this.hidden = true;
          this.limpar();
        });

        this.unsubVariante = subscribe(EV.variantChange, ({ data }) => {
          if (data.sectionId !== this.sectionId) return;
          this.atualizar(data.variant);
        });

        this.buscarContagem();
      }

      disconnectedCallback() {
        this.unsubSelecao?.();
        this.unsubVariante?.();
      }

      /**
       * Espelha o `get sectionId()` do product-info.js
       * (`originalSection || section`). Em quick view os dois divergem, e
       * comparar com o atributo errado faria o formulário ignorar o evento.
       */
      get sectionId() {
        const pi = this.closest('product-info');
        return pi?.dataset.originalSection || pi?.dataset.section || this.dataset.section;
      }

      /** O evento de seleção é global; só reajo ao da minha própria seção. */
      pertenceAMim(target) {
        if (!target) return true;
        const pi = this.closest('product-info');
        return !pi || pi.contains(target);
      }

      atualizar(variante) {
        const indisponivel = !!variante && !variante.available;
        this.hidden = !indisponivel;

        if (variante) {
          this.form.variantId.value = variante.id;
          this.limpar();
          if (indisponivel) this.buscarContagem();
        }
      }

      /** Zera mensagem e contagem — senão o "pronto!" de um sabor sobra no outro. */
      limpar() {
        this.msg.hidden = true;
        this.msg.textContent = '';
        this.count.hidden = true;
        this.form.hidden = false;
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.enviando) return;

        const email = this.form.email.value.trim();
        if (!email) {
          this.form.email.focus();
          return;
        }

        this.enviando = true;
        this.botao.disabled = true;
        this.botao.classList.add('is-loading');

        try {
          const res = await fetch(`${this.dataset.endpoint}/api/restock/subscribe`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              variantId: this.form.variantId.value,
              website:   this.form.website.value, // honeypot
            }),
          });

          const dados = await res.json().catch(() => ({}));
          if (!res.ok || dados.error) throw new Error(dados.error || 'falha');

          this.mostrarMensagem(this.dataset.sucesso, true);
          this.form.hidden = true; // some com o campo: a tarefa acabou
        } catch (err) {
          this.mostrarMensagem(this.dataset.erro, false);
        } finally {
          this.enviando = false;
          this.botao.disabled = false;
          this.botao.classList.remove('is-loading');
        }
      }

      mostrarMensagem(texto, sucesso) {
        this.msg.textContent = texto;
        this.msg.classList.toggle('ec-restock__msg--ok', sucesso);
        this.msg.classList.toggle('ec-restock__msg--erro', !sucesso);
        this.msg.hidden = false;
      }

      async buscarContagem() {
        if (this.dataset.provaSocial !== 'true' || this.hidden) return;

        const variantId = this.form.variantId.value;
        if (!variantId) return;

        try {
          const res = await fetch(
            `${this.dataset.endpoint}/api/restock/count?variantId=${encodeURIComponent(variantId)}`
          );
          const { total } = await res.json();

          // Abaixo de 5 o número trabalha contra: sugere que ninguém quer o produto.
          if (total >= 5) {
            this.count.textContent = `${total} pessoas já estão na fila deste sabor.`;
            this.count.hidden = false;
          }
        } catch {
          /* prova social é enfeite: falhar aqui não pode atrapalhar o cadastro */
        }
      }
    }
  );
}
